import React, { useState } from "react";

export interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}

interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  onChange?: (tabId: string) => void;
}

export function Tabs({ tabs, defaultTab, onChange }: TabsProps) {
  const [selectedTab, setSelectedTab] = useState(
    defaultTab || tabs[0]?.id || "",
  );

  const handleTabChange = (tabId: string) => {
    setSelectedTab(tabId);
    onChange?.(tabId);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let newIndex = index;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        newIndex = index > 0 ? index - 1 : tabs.length - 1;
        break;
      case "ArrowRight":
        event.preventDefault();
        newIndex = index < tabs.length - 1 ? index + 1 : 0;
        break;
      case "Home":
        event.preventDefault();
        newIndex = 0;
        break;
      case "End":
        event.preventDefault();
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    const newTab = tabs[newIndex];
    if (newTab) {
      handleTabChange(newTab.id);
      // Focus the newly selected tab
      const tabElement = document.querySelector(
        `[data-tab-id="${newTab.id}"]`,
      ) as HTMLButtonElement;
      tabElement?.focus();
    }
  };

  return (
    <div className="w-full">
      {/* Tab List */}
      <div
        role="tablist"
        className="flex gap-1 border-b border-gray-200 dark:border-zinc-700"
      >
        {tabs.map((tab, index) => {
          const isSelected = tab.id === selectedTab;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-tab-id={tab.id}
              role="tab"
              type="button"
              aria-selected={isSelected}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => handleTabChange(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`
                px-4 py-3 text-sm font-medium transition-colors cursor-pointer
                border-b-2 -mb-px flex items-center gap-2
                focus:outline-none focus:ring-2 focus:ring-primary rounded-t-lg
                ${
                  isSelected
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-zinc-600"
                }
              `}
            >
              {Icon && <Icon className="w-5 h-5" />}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Panel */}
      {tabs.map((tab) => {
        if (tab.id !== selectedTab) return null;
        return (
          <div
            key={tab.id}
            role="tabpanel"
            id={`panel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            tabIndex={0}
            className="py-6"
          >
            {tab.content}
          </div>
        );
      })}
    </div>
  );
}
